import { useState } from "react";
import { formatDateTime } from "../utils/formatters";

// Shows the Job Description a final resume was generated from, right next to
// that resume on the Optimization page — so anyone looking at the resume can
// see exactly which JD it was tailored to.
//
// Everything comes from the optimization document that's already loaded
// (opt.jobDescription, opt.requiredSkills, opt.preferredSkills), so this
// component makes no API calls.

const CARD_STYLE = {
  border: "1px solid #e0e0e0",
  borderRadius: "6px",
  padding: "14px 16px",
  backgroundColor: "#fff",
  boxSizing: "border-box",
  marginTop: "16px",
};

const LABEL_STYLE = { fontSize: "12px", fontWeight: "bold", color: "#5a5a5a", marginBottom: "6px" };

// How each JD skill ended up in the final resume.
//   IN_RESUME — the candidate already had it (PRESENT) or the recruiter
//               approved adding it ("Use in Optimized Resume" checked)
//   PARTIAL   — partly evidenced, not explicitly approved
//   NOT_IN    — missing / do-not-add / not approved
const SKILL_STATE_STYLES = {
  IN_RESUME: { bg: "#e6f4ea", fg: "#1e7e34", border: "#b7dfc1", label: "In resume" },
  PARTIAL: { bg: "#fff8e1", fg: "#8a6d00", border: "#f0dca0", label: "Partial" },
  NOT_IN: { bg: "#f4f5f7", fg: "#5a5a5a", border: "#e0e0e0", label: "Not in resume" },
};

function skillState(skill) {
  if (skill.selected || skill.status === "PRESENT") return "IN_RESUME";
  if (skill.status === "PARTIAL" || skill.status === "SKILLS_ONLY_MATCH") return "PARTIAL";
  return "NOT_IN";
}

// Uses the matched skill list (with statuses) when skill matching has run;
// otherwise falls back to the plain skill names parsed from the JD.
function buildSkillChips(matched, parsedNames) {
  if (Array.isArray(matched) && matched.length) {
    return matched.map((s) => ({ name: s.resolvedName || s.name, state: skillState(s) }));
  }
  return (parsedNames || []).map((name) => ({ name, state: null }));
}

function SkillChips({ title, chips }) {
  if (!chips.length) return null;
  return (
    <div style={{ marginBottom: "12px" }}>
      <div style={LABEL_STYLE}>{title}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {chips.map((chip, i) => {
          const style = chip.state ? SKILL_STATE_STYLES[chip.state] : SKILL_STATE_STYLES.NOT_IN;
          return (
            <span
              key={`${chip.name}-${i}`}
              title={chip.state ? style.label : undefined}
              style={{
                fontSize: "12px",
                padding: "3px 10px",
                borderRadius: "12px",
                backgroundColor: style.bg,
                color: style.fg,
                border: `1px solid ${style.border}`,
              }}
            >
              {chip.name}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function SkillLegend() {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", fontSize: "12px", color: "#5a5a5a", marginBottom: "12px" }}>
      {Object.values(SKILL_STATE_STYLES).map((s) => (
        <span key={s.label} style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
          <span
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: s.bg,
              border: `1px solid ${s.fg}`,
              display: "inline-block",
            }}
          />
          {s.label}
        </span>
      ))}
    </div>
  );
}

export default function FinalResumeJdCard({ opt }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const jd = opt?.jobDescription || {};
  const rawText = (jd.rawText || "").trim();
  const clientName = opt?.clientId?.name; // populated by GET /optimization/:id

  const requiredChips = buildSkillChips(opt?.requiredSkills, jd.requiredSkills);
  const preferredChips = buildSkillChips(opt?.preferredSkills, jd.preferredSkills);
  const hasStatuses = requiredChips.some((c) => c.state) || preferredChips.some((c) => c.state);

  // Nothing to show at all — e.g. an old draft saved without a JD.
  if (!rawText && !jd.title && !requiredChips.length && !preferredChips.length) {
    return (
      <div style={CARD_STYLE}>
        <div style={LABEL_STYLE}>JOB DESCRIPTION USED FOR THIS RESUME</div>
        <p style={{ margin: 0, fontSize: "14px", color: "#5a5a5a" }}>No job description was saved with this optimization.</p>
      </div>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(rawText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div style={CARD_STYLE}>
      <div
        style={{
          fontSize: "12px",
          fontWeight: "bold",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: "#5a5a5a",
          marginBottom: "10px",
        }}
      >
        Job Description used for this resume
      </div>

      {/* Header: title, client, link, experience, when the resume was generated */}
      <div style={{ marginBottom: "12px" }}>
        <div style={{ fontSize: "16px", fontWeight: "bold", color: "#1a4d8f" }}>{jd.title || "Untitled role"}</div>
        <div style={{ fontSize: "13px", color: "#3a3a3a", marginTop: "4px", display: "flex", flexWrap: "wrap", gap: "4px 16px" }}>
          {clientName && (
            <span>
              <strong>Client:</strong> {clientName}
            </span>
          )}
          {jd.requiredExperience != null && (
            <span>
              <strong>Required experience:</strong> {jd.requiredExperience}+ years
            </span>
          )}
          {opt?.updatedAt && (
            <span>
              <strong>Resume generated:</strong> {formatDateTime(opt.updatedAt)}
            </span>
          )}
          {jd.url && (
            <a href={jd.url} target="_blank" rel="noopener noreferrer">
              View original job posting
            </a>
          )}
        </div>
      </div>

      {/* Skills from the JD, coloured by whether they made it into the resume */}
      {hasStatuses && <SkillLegend />}
      <SkillChips title="Required skills (from JD)" chips={requiredChips} />
      <SkillChips title="Preferred skills (from JD)" chips={preferredChips} />

      {/* Full JD text */}
      {rawText && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px", gap: "8px", flexWrap: "wrap" }}>
            <div style={{ ...LABEL_STYLE, marginBottom: 0 }}>Full job description</div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button type="button" className="btn-secondary" style={{ padding: "4px 12px", fontSize: "12px" }} onClick={handleCopy}>
                {copied ? "Copied" : "Copy JD"}
              </button>
              <button
                type="button"
                className="btn-secondary"
                style={{ padding: "4px 12px", fontSize: "12px" }}
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? "Show less" : "Show full JD"}
              </button>
            </div>
          </div>
          <div
            style={{
              whiteSpace: "pre-wrap", // keeps the JD's own line breaks and bullets
              wordBreak: "break-word",
              fontSize: "13px",
              lineHeight: 1.5,
              color: "#3a3a3a",
              backgroundColor: "#f7f9fb",
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              padding: "10px 12px",
              maxHeight: expanded ? "600px" : "160px",
              overflowY: "auto",
            }}
          >
            {rawText}
          </div>
        </div>
      )}
    </div>
  );
}
